import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Plus, Target, Flame, Check, Calendar, TrendingUp,
  MoreHorizontal, Trash2, Loader2, Zap
} from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "@/lib/authFetch";
import NotificationBell from "@/components/shared/NotificationBell";  // FaseC C1

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Security audit (FIN-2 T1, 2026-04-30) — sections legacy.
 *
 * Backend já blindado pela SEC-1. Sem JWT, fetch retorna 401 e UI quebra.
 *
 * | Endpoint chamado            | authFetch usado? | Notas             |
 * |-----------------------------|------------------|-------------------|
 * | GET  /api/habits            | ❌ → ✅ (T2)     | listar hábitos    |
 * | POST /api/habits            | ❌ → ✅ (T2)     | criar hábito      |
 * | POST /api/habits/:id/checkin| ❌ → ✅ (T2)     | check-in          |
 * | DEL  /api/habits/:id        | ❌ → ✅ (T2)     | deletar hábito    |
 */

// Habit Card Component
function HabitCard({ habit, onCheckIn, onDelete }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const checkedToday = habit.last_checkin && 
    new Date(habit.last_checkin).toDateString() === new Date().toDateString();

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      whileHover={{ y: -2 }}
      className={`
        bg-nectar-card border rounded-xl p-4 transition-all duration-300
        ${checkedToday ? "border-brand-green/30" : "border-nectar-border hover:border-status-warning/30"}
      `}
      data-testid={`habit-card-${habit._id || habit.id}`}
    >
      <div className="flex items-center gap-4">
        {/* Check-in Button */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => !checkedToday && onCheckIn(habit)}
          disabled={checkedToday}
          className={`
            w-12 h-12 rounded-xl flex items-center justify-center transition-all
            ${checkedToday 
              ? "bg-brand-green/20 text-brand-green cursor-default" 
              : "bg-status-warning/10 text-status-warning hover:bg-status-warning/20 cursor-pointer"
            }
          `}
        >
          {checkedToday ? <Check className="w-6 h-6" /> : <Target className="w-6 h-6" />}
        </motion.button>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-medium">{habit.name}</h4>
          <div className="flex items-center gap-3 mt-1">
            <span className="flex items-center gap-1 text-xs text-status-warning">
              <Flame className="w-3 h-3" />
              {habit.streak || 0} dias de streak
            </span>
            <span className="text-xs text-slate-500">
              {habit.frequency === 'daily' ? 'Diário' : 
               habit.frequency === 'weekly' ? 'Semanal' : habit.frequency}
            </span>
          </div>
        </div>

        {/* Stats */}
        <div className="text-right mr-2">
          <p className="text-2xl font-bold text-white">{habit.total_checkins || 0}</p>
          <p className="text-xs text-slate-500">check-ins</p>
        </div>

        {/* Menu */}
        <div className="relative">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-2 rounded-lg hover:bg-white/5 text-slate-500 hover:text-white transition-colors"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>

          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="absolute right-0 top-10 bg-nectar-card border border-nectar-border rounded-lg shadow-xl z-10 py-1 min-w-[120px]"
              >
                <button
                  onClick={() => { onDelete(habit); setIsMenuOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-status-error hover:bg-status-error/10 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Excluir
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Progress Bar */}
      {habit.streak > 0 && (
        <div className="mt-3 pt-3 border-t border-nectar-border">
          <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
            <span>Progresso semanal</span>
            <span>{Math.min(habit.streak, 7)}/7</span>
          </div>
          <div className="h-1.5 bg-nectar-bg rounded-full overflow-hidden">
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: `${Math.min((habit.streak / 7) * 100, 100)}%` }}
              className="h-full bg-gradient-to-r from-status-warning to-brand-green rounded-full"
            />
          </div>
        </div>
      )}
    </motion.div>
  );
}

// Create Habit Modal
function CreateHabitModal({ isOpen, onClose, onSubmit }) {
  const [name, setName] = useState("");
  const [frequency, setFrequency] = useState("daily");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName("");
      setFrequency("daily");
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setIsSubmitting(true);
    await onSubmit({ name, frequency });
    setIsSubmitting(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-nectar-card border border-nectar-border rounded-2xl p-6 safe-modal-pb w-full max-w-md"
          >
            <h3 className="font-display text-xl font-bold text-white mb-4">Novo Hábito</h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Nome do Hábito</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Meditar, Exercitar, Ler..."
                  className="w-full px-4 py-3 bg-nectar-bg border border-nectar-border rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-status-warning/50 transition-colors"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Frequência</label>
                <select
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                  className="w-full px-4 py-3 bg-nectar-bg border border-nectar-border rounded-xl text-white focus:outline-none focus:border-status-warning/50 transition-colors"
                >
                  <option value="daily">Diário</option>
                  <option value="weekly">Semanal</option>
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-3 rounded-xl border border-nectar-border text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={!name.trim() || isSubmitting}
                  className="flex-1 px-4 py-3 rounded-xl bg-status-warning/10 border border-status-warning/30 text-status-warning hover:bg-status-warning/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Criar
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Main Habits Section
export default function HabitsSection() {
  const [habits, setHabits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchHabits = useCallback(async () => {
    try {
      const response = await authFetch(`${API_URL}/api/habits`);
      if (response.ok) {
        const data = await response.json();
        setHabits(data.habits || []);
      }
    } catch (error) {
      console.error("Error fetching habits:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHabits();
  }, [fetchHabits]);

  const handleCreate = async (data) => {
    try {
      const response = await authFetch(`${API_URL}/api/habits`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        toast.success("Hábito criado!");
        fetchHabits();
      }
    } catch (error) {
      toast.error("Erro ao criar hábito");
    }
  };

  const handleCheckIn = async (habit) => {
    try {
      const response = await authFetch(`${API_URL}/api/habits/${habit._id || habit.id}/checkin`, {
        method: "POST",
      });
      if (response.ok) {
        toast.success("Check-in realizado! 🔥");
        fetchHabits();
      }
    } catch (error) {
      toast.error("Erro no check-in");
    }
  };

  const handleDelete = async (habit) => {
    if (!window.confirm("Excluir este hábito?")) return;
    try {
      const response = await authFetch(`${API_URL}/api/habits/${habit._id || habit.id}`, {
        method: "DELETE",
      });
      if (response.ok) {
        setHabits(prev => prev.filter(h => (h._id || h.id) !== (habit._id || habit.id)));
        toast.success("Hábito excluído");
      }
    } catch (error) {
      console.error("Error deleting habit:", error);
    }
  };

  const totalStreak = habits.reduce((sum, h) => sum + (h.streak || 0), 0);
  const checkedToday = habits.filter(h => 
    h.last_checkin && new Date(h.last_checkin).toDateString() === new Date().toDateString()
  ).length;

  return (
    <div className="p-6 md:p-8 pb-32">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-wide">Hábitos</h1>
          <p className="text-slate-400 mt-1">
            {checkedToday}/{habits.length} completados hoje
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <NotificationBell />
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-5 py-3 rounded-xl bg-status-warning/10 border border-status-warning/30 text-status-warning hover:bg-status-warning/20 transition-all"
            data-testid="create-habit-btn"
          >
            <Plus className="w-5 h-5" />
            Novo Hábito
          </motion.button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-nectar-card border border-nectar-border rounded-xl p-4">
          <div className="flex items-center gap-2 text-status-warning mb-2">
            <Flame className="w-5 h-5" />
            <span className="text-sm">Total de Streaks</span>
          </div>
          <p className="text-2xl font-bold text-white">{totalStreak} dias</p>
        </div>
        <div className="bg-nectar-card border border-nectar-border rounded-xl p-4">
          <div className="flex items-center gap-2 text-brand-green mb-2">
            <TrendingUp className="w-5 h-5" />
            <span className="text-sm">Taxa de Sucesso</span>
          </div>
          <p className="text-2xl font-bold text-white">
            {habits.length > 0 ? Math.round((checkedToday / habits.length) * 100) : 0}%
          </p>
        </div>
        <div className="bg-nectar-card border border-nectar-border rounded-xl p-4 col-span-2 md:col-span-1">
          <div className="flex items-center gap-2 text-brand-cyan mb-2">
            <Zap className="w-5 h-5" />
            <span className="text-sm">Hábitos Ativos</span>
          </div>
          <p className="text-2xl font-bold text-white">{habits.length}</p>
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-status-warning animate-spin" />
        </div>
      ) : habits.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20">
          <div className="w-16 h-16 rounded-2xl bg-status-warning/10 flex items-center justify-center mx-auto mb-4">
            <Target className="w-8 h-8 text-status-warning" />
          </div>
          <h3 className="text-xl font-medium text-white mb-2">Nenhum hábito ainda</h3>
          <p className="text-slate-500 mb-6">Comece a construir bons hábitos hoje</p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-6 py-3 rounded-xl bg-status-warning/10 border border-status-warning/30 text-status-warning hover:bg-status-warning/20 transition-all"
          >
            Criar Hábito
          </button>
        </motion.div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence mode="popLayout">
            {habits.map(habit => (
              <HabitCard
                key={habit._id || habit.id}
                habit={habit}
                onCheckIn={handleCheckIn}
                onDelete={handleDelete}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      <CreateHabitModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreate}
      />
    </div>
  );
}
